import { navigate, PageProps } from 'gatsby';
import { ReactNode, useEffect } from 'react';
import { Layout } from 'components/layout/Layout';
import { PublicLayout } from 'components/layout/PublicLayout';
import { ResultsLayout } from 'components/layout/ResultsLayout';
import { LoadingSpinner } from 'components/LoadingSpinner';
import { ServerError } from 'components/errorPages/ServerError';
import { AccessError } from 'components/errorPages/AccessError';
import { RoleAccessDenied } from 'components/errorPages/RoleAccessDenied';
import { NotFound } from 'components/errorPages/NotFound';
import { useTheme } from 'hooks/useTheme';
import { useUser } from 'services/User';
import { useUserData } from 'services/api/UserData';
import { canAccessPath, shouldRedirectToDashboard } from 'services/accessControl';
import { DashboardTabs } from 'services/tabs';

import '@opswat/ui/styles';
import '../../tailwind.css';
import 'components/WrapPage.css';

const PUBLIC_ROUTES = ['/', '/extension/get-apikey', '/product'];
const GUEST_PUBLIC_ROUTE_PREFIXES = ['/integrations'];
const AUTH_OPTIONAL_ROUTES = ['/licensing'];

const HIDE_AS_NOT_FOUND_PREFIXES = ['/store'];

type WrapPageProps = Omit<PageProps, 'children'> & { children?: ReactNode };

// The 404 page is served both at `/404` directly and as the fallback for any unknown
// route (where `location.pathname` is the attempted URL but `path` is the 404 page).
const isNotFoundPage = (pathname: string, path?: string): boolean =>
    pathname === '/404' || pathname === '/404.html' || path === '/404' || path === '/404/' || path === '/404.html';

export const WrapPage = ({ children, ...props }: WrapPageProps) => {
    const pathname = props.location.pathname;
    const isPublic = PUBLIC_ROUTES.includes(pathname);
    const isAuthOptional = AUTH_OPTIONAL_ROUTES.includes(pathname);
    const { user } = useUser();
    const { error, isLoading } = useUserData({
        onSuccess: () => {
            if (pathname.startsWith('/extension/get-apikey') && window) {
                window.close();
            }
        },
    });

    useTheme();

    useEffect(() => {
        document.querySelector('main')?.scrollTo({ top: 0 });
        window.scrollTo({ top: 0 });
    }, [pathname]);

    // Skip the home->dashboard redirect when the user explicitly clicked the Home button.
    const cameFromHomeButton = Boolean((props.location.state as { fromHomeButton?: boolean } | null)?.fromHomeButton);
    const redirectHomeToDashboard = pathname === '/' && !cameFromHomeButton && shouldRedirectToDashboard(user);
    useEffect(() => {
        if (redirectHomeToDashboard) {
            navigate(`/dashboard/${DashboardTabs.Security}`);
        }
    }, [redirectHomeToDashboard]);

    if (isLoading || redirectHomeToDashboard) {
        return <LoadingSpinner />;
    }

    // The 404 page uses the public layout — same as Product / Integrations — so no sidebar.
    if (isNotFoundPage(pathname, props.path)) {
        return <PublicLayout {...props}>{children}</PublicLayout>;
    }

    if (isPublic || (isAuthOptional && user?.isLoggedIn !== true)) {
        return <PublicLayout {...props}>{children}</PublicLayout>;
    }

    if (error?.response?.data?.code === 403004) {
        return (
            <PublicLayout {...props}>
                <AccessError />
            </PublicLayout>
        );
    }

    if (error?.response?.data?.code === 403002) {
        return (
            <PublicLayout {...props}>
                <AccessError link={false} />
            </PublicLayout>
        );
    }
    if (error) {
        return (
            <PublicLayout {...props}>
                <ServerError />
            </PublicLayout>
        );
    }

    const isGuestPublicRoute = GUEST_PUBLIC_ROUTE_PREFIXES.some((prefix) => pathname.startsWith(prefix));
    if (
        !isPublic &&
        !isAuthOptional &&
        !isGuestPublicRoute &&
        !pathname.startsWith('/results') &&
        !pathname.startsWith('/blog')
    ) {
        if (user?.isLoggedIn !== true) {
            return (
                <PublicLayout {...props}>
                    <NotFound />
                </PublicLayout>
            );
        }
    }

    const matchesHideAsNotFound = HIDE_AS_NOT_FOUND_PREFIXES.some((prefix) => pathname.startsWith(prefix));

    if (matchesHideAsNotFound && !canAccessPath(user, pathname)) {
        return (
            <PublicLayout {...props}>
                <NotFound />
            </PublicLayout>
        );
    }

    if (user && !canAccessPath(user, pathname)) {
        return (
            <PublicLayout {...props}>
                <RoleAccessDenied />
            </PublicLayout>
        );
    }

    if (pathname.startsWith('/results')) {
        return <ResultsLayout {...props}>{children}</ResultsLayout>;
    }

    if (pathname.startsWith('/blog')) {
        return <PublicLayout {...props}>{children}</PublicLayout>;
    }

    if (isGuestPublicRoute && !user?.isLoggedIn) {
        return <PublicLayout {...props}>{children}</PublicLayout>;
    }

    return <Layout {...props}>{children}</Layout>;
};
